/**
 * Step model for the guided new-employee flow. Pure so the ordering,
 * gating and skip rules can be tested without rendering React.
 *
 * The person is created at the end of step 1. Every later step then drives
 * the existing idempotent per-person assignment endpoints, which is also
 * what makes the flow resumable: abandoning it half way leaves a real
 * person record that can be finished from their onboarding tab.
 */

export type StepId =
  | 'details'
  | 'template'
  | 'training'
  | 'documents'
  | 'forms'
  | 'tasks'
  | 'review';

export interface StepDef {
  id: StepId;
  label: string;
  /** Shown under the step heading. */
  description: string;
}

export const WIZARD_STEPS: readonly StepDef[] = [
  {
    id: 'details',
    label: 'Details',
    description: "Who they are. Their job role drives everything that follows.",
  },
  {
    id: 'template',
    label: 'Role template',
    description: 'What their role automatically assigned, and a chance to re-apply it.',
  },
  {
    id: 'training',
    label: 'Training',
    description: 'E-learning modules they need to complete.',
  },
  {
    id: 'documents',
    label: 'Documents',
    description: 'Documents they need to read and sign.',
  },
  {
    id: 'forms',
    label: 'Forms',
    description: 'Onboarding forms they need to fill in.',
  },
  {
    id: 'tasks',
    label: 'Tasks',
    description: 'Anything else they need to do or provide.',
  },
  {
    id: 'review',
    label: 'Review',
    description: 'Check the setup and send their welcome email.',
  },
] as const;

export function stepIndex(id: StepId): number {
  return WIZARD_STEPS.findIndex((s) => s.id === id);
}

export function stepDef(id: StepId): StepDef {
  const def = WIZARD_STEPS.find((s) => s.id === id);
  if (!def) throw new Error(`unknown wizard step: ${id}`);
  return def;
}

/**
 * A step is reachable once the person exists; before that only Details is,
 * because every other step needs a person id to assign against. Prevents a
 * deep link or a stray Next from landing on a step with nothing to act on.
 */
export function stepReachable(id: StepId, personId: string | null): boolean {
  return id === 'details' || personId !== null;
}

export function nextStep(id: StepId): StepId | null {
  const i = stepIndex(id);
  return i >= 0 && i < WIZARD_STEPS.length - 1 ? WIZARD_STEPS[i + 1]!.id : null;
}

export function prevStep(id: StepId): StepId | null {
  const i = stepIndex(id);
  return i > 0 ? WIZARD_STEPS[i - 1]!.id : null;
}

/**
 * The assignment steps are optional by design: a starter whose role template
 * already covers everything needs no manual ticking, so Next reads "Skip"
 * once nothing has changed on the step. Details and Review always require a
 * deliberate action.
 */
export function stepSkippable(id: StepId): boolean {
  return id !== 'details' && id !== 'review';
}
